import _ from 'lodash'
import type { Unref } from '@wix/thunderbolt-catharsis'
import { Materializer, Update, Transform, createRef } from '@wix/materializer'
import { getDisplayedId, getFullId, getFullItemId, getUniqueId, REPEATER_DELIMITER } from '@wix/thunderbolt-commons'
import { Component, DataMapName, pointers, QueryProperty, ViewMode } from '@wix/thunderbolt-becky-types'
import type { RawModel } from '../../types'
import type {
	ConsumeRefParams,
	DataItemWithOverridesRefs,
	GetLeafsOverridesMapsRefs,
	GetOverrideDataRefs,
	GetOverrideTranslationDataRefs,
	PresetsRefsWithAncestors,
} from './types'
import {
	isRefComponent,
	getRefCreatorInNamespace,
	getLeafsOverridesMapAspectName,
	getContextAndTemplateCompIdFromInflatedCompId,
	isLeafOverride,
} from '../byRefUtils'
import {
	DATA_MAP_NAMES,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
	QUERY_MAPPING,
	REF_COMPONENTS_TEMPLATE_DATA_ASPECT,
	EMPTY_SCHEMA,
	EMPTY_REMOVED,
	RESOLVED_REF_COMP_PRESETS_ASPECT,
	STRUCTURE_ASPECT,
	COMP_ID_TO_INFLATED_COMP_ID_ASPECT,
	ENVIRONMENT_NAMESPACE,
	RESOLVED_TRANSLATIONS_ASPECT,
	ROOT_COMP_ID_ASPECT,
	DATA_QUERY_TO_COMP_ID_ASPECT,
	INFLATED_COMP_ID_TO_TEMPLATE_COMP,
	INVALIDATIONS_NAMESPACE,
	INFLATED_MAP_TO_DATA_MAP_NAME,
} from '../constants'
import { getDataItemRefsWithOverrides, resolveOverrideItem } from './dataConsumption'
import { inflateCompStructure } from './inflateCompStructure'
import { getPresetsRefsWithAncestors, getRefCompResolvedPresetsRef } from './presetsResolving'
import { consumptionCompNodes, consumptionDataNodes } from './consumptionNodes'

const RETURN_EMPTY_ARRAY_FN = () => []

const queryNames = Object.keys(QUERY_MAPPING).filter(
	(queryName) => queryName !== 'presetsQuery'
) as Array<QueryProperty>

const {
	data: {
		DATA_MAPS: { SLOTS },
	},
	components: {
		PROPERTY_TYPES: { DATA_QUERY, DESIGN_QUERY },
	},
	QUERY_TO_MAP_NAME,
} = pointers

export const getRefConsumptionTransformer = (rawModel: RawModel): Transform => (value, path) => {
	if (!value) {
		return value
	}

	const [, aspect, id] = path

	if (aspect === RESOLVED_REF_COMP_PRESETS_ASPECT) {
		const { ownPresets, parentRefCompResolvedPresets } = value as Unref<PresetsRefsWithAncestors>
		const ownPresetsValues = ownPresets.flatMap((v) => (v?.type === 'RefArray' ? v.values : v ? [v] : []))
		return [ownPresetsValues, ...parentRefCompResolvedPresets]
	}

	if (aspect in consumptionCompNodes) {
		const compStructure = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, id])
		return consumptionCompNodes[aspect].toViewItem(compStructure, value)
	}

	const fixedAspect = INFLATED_MAP_TO_DATA_MAP_NAME[aspect] || aspect
	if (DATA_MAP_NAMES.has(fixedAspect as DataMapName)) {
		if (consumptionDataNodes[fixedAspect as DataMapName]?.toViewItem) {
			return consumptionDataNodes[fixedAspect as DataMapName]?.toViewItem!({ id, aspect, value, rawModel })
		}

		const { dataItemWithOverrides } = value as Unref<DataItemWithOverridesRefs>
		return resolveOverrideItem(dataItemWithOverrides, id)
	}

	return value
}

export const createBatchedRefConsumer = (materializer: Materializer, rawModel: RawModel, update: Update<void>) => {
	const viewMode = materializer.get<ViewMode>([ENVIRONMENT_NAMESPACE, ENVIRONMENT_NAMESPACE, 'viewMode'])
	const isMobileView = viewMode === 'mobile'

	const inflatedRepeatedDataItemIds: Record<string, true> = {}

	const inflateWidgetTemplate = ({
		pageId,
		templateKey,
		rootCompId,
		ancestorsChain,
		getOverrideDataRefs,
		getOverrideTranslationDataRefs,
		getLeafsOverridesMapsRefs,
	}: {
		templateKey: string
		rootCompId: string
		ancestorsChain: string
		pageId: string
		getOverrideDataRefs: GetOverrideDataRefs
		getOverrideTranslationDataRefs: GetOverrideTranslationDataRefs
		getLeafsOverridesMapsRefs: GetLeafsOverridesMapsRefs
	}) => {
		const createRefInTemplateNamespace = getRefCreatorInNamespace(templateKey)
		const getParentRefCompResolvedPresets = () => getRefCompResolvedPresetsRef(ancestorsChain)

		const inflateDataItem = (
			inflatedDataItemId: string,
			queryName: QueryProperty,
			templateComponent: Component,
			inflatedComponent: Component,
			itemSuffix = ''
		) => {
			const dataMapName = QUERY_MAPPING[queryName]

			const getDependenciesData = {
				rawModel,
				isMobileView,
				queryName,
				widgetNamespace: templateKey,
				templateComponent,
				getOverrideDataRefs,
				createRefInTemplateNamespace,
				getOverrideTranslationDataRefs,
				getLeafsOverridesMapsRefs,
				getParentRefCompResolvedPresets,
				itemSuffix,
			}

			const dependencies = consumptionDataNodes[dataMapName]?.getDependencies?.(getDependenciesData) || {
				dataItemWithOverrides: getDataItemRefsWithOverrides(getDependenciesData),
			}

			if (consumptionDataNodes[dataMapName]?.updateDependencies) {
				consumptionDataNodes[dataMapName]!.updateDependencies!({ update, inflatedComponent, dependencies })
			} else {
				update(RESOLVED_REFS_NAMESPACE, dataMapName, inflatedDataItemId, dependencies, {
					schema: dependencies,
					removed: EMPTY_REMOVED,
				})
			}
		}

		const inflateRepeatedDataItems = () => {
			;[DATA_QUERY, DESIGN_QUERY].forEach((queryName) => {
				const data = _.get(rawModel, [templateKey, QUERY_TO_MAP_NAME[queryName]])
				_.forEach(data, (__, dataItemId) => {
					const repeatedItemScope = getFullItemId(dataItemId) // dataItem-ksoawjjj__itemInner__itemOuter => itemInner__itemOuter
					if (!repeatedItemScope) {
						return
					}

					const repeatedItemTemplateId = getFullId(dataItemId) // comp-ksoaxbli_r_comp-ksekvwbd-dataQuery or dataItem-ksoawjjj
					const compId = repeatedItemTemplateId.endsWith(queryName)
						? repeatedItemTemplateId.split(`-${queryName}`)[0]
						: _.get(rawModel, [templateKey, DATA_QUERY_TO_COMP_ID_ASPECT, repeatedItemTemplateId])

					const inflatedCompId = getUniqueId(ancestorsChain, compId)
					const inflatedComp = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, inflatedCompId])
					const inflatedDataItemId = getDisplayedId(inflatedComp[queryName], repeatedItemScope)

					if (inflatedRepeatedDataItemIds[inflatedDataItemId]) {
						// if the component data under a certain repeater item suffix is already inflated, return, otherwise an outer override will override the inner overrides.
						return
					}

					const templateComp = isLeafOverride(compId)
						? _.get(rawModel, [templateKey, STRUCTURE_ASPECT, compId])
						: _.get(rawModel, [INFLATED_COMP_ID_TO_TEMPLATE_COMP, inflatedCompId])

					inflateDataItem(
						inflatedDataItemId,
						queryName,
						templateComp,
						inflatedComp,
						`${REPEATER_DELIMITER}${repeatedItemScope}`
					)

					inflatedRepeatedDataItemIds[inflatedDataItemId] = true
				})
			})
		}

		const inflateSlotComponents = () => {
			const slotsData = _.get(rawModel, [templateKey, SLOTS], {})
			_(slotsData)
				.flatMap((slotsDataItem) => _.values(slotsDataItem.slots))
				.uniq()
				.forEach((id) => inflateTemplateStructure(id))
		}

		const inflateTemplateStructure = (compId: string, isRootComp: boolean = false): void => {
			const templateComp = _.get(rawModel, [templateKey, STRUCTURE_ASPECT, compId])

			if (!templateComp) {
				return
			}

			if (Array.isArray(templateComp.components)) {
				for (const childId of templateComp.components) {
					inflateTemplateStructure(childId)
				}
			}

			const inflatedComp = inflateCompStructure({
				rawModel,
				pageId,
				templateComp,
				ancestorsChain,
				isRootComp,
				viewMode,
			})

			if (isRefComponent(inflatedComp)) {
				const refCompChildId = consumeRef({
					refCompId: compId,
					refCompAncestorTemplateKey: templateKey,
					refCompAncestorsChain: ancestorsChain,
					pageId,
					getAncestorsOverrideDataRefs: getOverrideDataRefs,
					getAncestorsOverrideTranslationDataRefs: getOverrideTranslationDataRefs,
					getAncestorsLeafsOverridesMapsRefs: getLeafsOverridesMapsRefs,
				})
				inflatedComp.components = [refCompChildId]
			}

			update(RESOLVED_REFS_NAMESPACE, COMP_ID_TO_INFLATED_COMP_ID_ASPECT, compId, inflatedComp.id, EMPTY_SCHEMA)
			_.set(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, inflatedComp.id], inflatedComp)
			_.set(rawModel, [INFLATED_COMP_ID_TO_TEMPLATE_COMP, inflatedComp.id], templateComp)

			for (const refConsumptionCompNodeAspect in consumptionCompNodes) {
				const dependencies = consumptionCompNodes[refConsumptionCompNodeAspect].getDependencies(inflatedComp)
				update(RESOLVED_REFS_NAMESPACE, refConsumptionCompNodeAspect, inflatedComp.id, dependencies, {
					schema: dependencies,
					removed: EMPTY_REMOVED,
				})
			}

			_.forEach(queryNames, (queryName) => {
				if (
					templateComp[queryName] ||
					queryName === 'propertyQuery' ||
					queryName === 'connectionQuery' // there may be connections overrides even if the template component does not have a connectionQuery
				) {
					if (templateComp[queryName] === inflatedComp[queryName]) {
						return
					}
					inflateDataItem(inflatedComp[queryName], queryName, templateComp, inflatedComp)
				}
			})
		}

		// 1. DFS all the structure of {templateKey}, inflate all comps in the context of {ancestorsChain}
		inflateTemplateStructure(rootCompId, true)
		// 2. Inflate all orphan (not pointed by structure) components and data items
		inflateSlotComponents() // TODO slots are orphan components in the flat structure so inflateTemplateStructure() won't reach them. remove this line once the filter() is removed from flattenStructure.ts.
		inflateRepeatedDataItems()
	}

	const consumeRef = ({
		refCompId,
		refCompAncestorTemplateKey,
		refCompAncestorsChain,
		pageId,
		getAncestorsOverrideDataRefs,
		getAncestorsOverrideTranslationDataRefs,
		getAncestorsLeafsOverridesMapsRefs,
	}: ConsumeRefParams) => {
		const createRefInRefCompNamespace = getRefCreatorInNamespace(refCompAncestorTemplateKey)

		const getOverrideDataRefs: GetOverrideDataRefs = ({
			compId,
			queryName,
			itemSuffix = '',
			templateDataId = '',
		}) => {
			const dataMapName = QUERY_MAPPING[queryName]
			const inflatedCompId = getUniqueId(refCompId, compId)

			return [
				...getAncestorsOverrideDataRefs({ compId: inflatedCompId, queryName, templateDataId, itemSuffix }),
				createRefInRefCompNamespace(
					dataMapName,
					`${inflatedCompId}-${templateDataId ? templateDataId : queryName}${itemSuffix}`
				),
			]
		}

		const getOverrideTranslationDataRefs: GetOverrideTranslationDataRefs = (compId: string, itemSuffix = '') => {
			const inflatedCompId = getUniqueId(refCompId, compId)

			return [
				...getAncestorsOverrideTranslationDataRefs(inflatedCompId, itemSuffix),
				createRefInRefCompNamespace(RESOLVED_TRANSLATIONS_ASPECT, `${inflatedCompId}-dataQuery${itemSuffix}`),
			]
		}

		const getLeafsOverridesMapsRefs: GetLeafsOverridesMapsRefs = (inflatedCompIdInNamespace, queryName) => {
			/**
			 * there are 2 options for `inflatedCompIdInNamespace`:
			 * 1. 	If it's the first one in the recursion, it is the template comp id.
			 * 		In this case, the context is the parentRefCompId
			 * 2.	If it's further into the recursion, it is a partially inflated comp id.
			 * 		Meaning that it is inflated up to this point (e.g. outerRefCompId_r_templateCompId).
			 * 		In this case, we first inflate it to the current level (e.g. parentRefCompId_r_outerRefCompId_r_templateCompId),
			 * 		then extract the context (parentRefCompId_r_outerRefCompId) and the templateCompId
			 */
			const inflatedCompId = getUniqueId(refCompId, inflatedCompIdInNamespace)
			const { context, templateCompId } = getContextAndTemplateCompIdFromInflatedCompId(inflatedCompId)
			return [
				...getAncestorsLeafsOverridesMapsRefs(inflatedCompId, queryName),
				createRefInRefCompNamespace(getLeafsOverridesMapAspectName(context, queryName), templateCompId),
			]
		}

		const innerWidgetTemplateData = materializer.get([
			refCompAncestorTemplateKey,
			REF_COMPONENTS_TEMPLATE_DATA_ASPECT,
			refCompId,
		])

		const { templateKey: innerWidgetTemplateKey, sharedPart, rootCompId } = innerWidgetTemplateData

		const templateDependency = createRef([INVALIDATIONS_NAMESPACE, INVALIDATIONS_NAMESPACE, innerWidgetTemplateKey])
		update(
			refCompAncestorTemplateKey,
			INVALIDATIONS_NAMESPACE,
			refCompId,
			{ templateDependency },
			{ schema: { templateDependency }, removed: EMPTY_REMOVED }
		)

		const innerWidgetRootCompId = sharedPart
			? rootCompId
			: _.get(rawModel, [innerWidgetTemplateKey, ROOT_COMP_ID_ASPECT])
		const innerWidgetAncestorsChain = refCompAncestorsChain
			? getUniqueId(refCompAncestorsChain, refCompId)
			: refCompId

		if (!sharedPart) {
			inflateWidgetTemplate({
				pageId,
				templateKey: innerWidgetTemplateKey,
				rootCompId: innerWidgetRootCompId,
				ancestorsChain: innerWidgetAncestorsChain,
				getOverrideDataRefs,
				getOverrideTranslationDataRefs,
				getLeafsOverridesMapsRefs,
			})
		}

		const refComp = _.get(rawModel, [refCompAncestorTemplateKey, STRUCTURE_ASPECT, refCompId])
		if (refComp.presetsQuery) {
			const presetsRefsWithAncestors = getPresetsRefsWithAncestors(
				refComp,
				refCompAncestorsChain,
				createRefInRefCompNamespace,
				getAncestorsOverrideDataRefs
			)
			update(
				RESOLVED_REFS_NAMESPACE,
				RESOLVED_REF_COMP_PRESETS_ASPECT,
				innerWidgetAncestorsChain,
				presetsRefsWithAncestors,
				{
					schema: presetsRefsWithAncestors,
					removed: EMPTY_REMOVED,
				}
			)
		}

		const inflatedWidgetRootCompId = getUniqueId(innerWidgetAncestorsChain, innerWidgetRootCompId)
		const refCompChildId = sharedPart ? innerWidgetRootCompId : inflatedWidgetRootCompId
		_.set(rawModel, [RESOLVED_REFS_NAMESPACE, innerWidgetAncestorsChain], refCompChildId)

		if (refCompAncestorTemplateKey === SITE_NAMESPACE) {
			// the refComp is in the page json so its components array hasn't been populated yet
			const resolvedRefComp =
				sharedPart && !_.get(rawModel, [SITE_NAMESPACE, STRUCTURE_ASPECT, refCompChildId])
					? {
							...refComp,
							componentType: 'DivWithChildren',
							components: [],
					  }
					: {
							...refComp,
							components: [refCompChildId],
					  }

			_.set(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, refCompId], resolvedRefComp)
			update(RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, refCompId, resolvedRefComp, EMPTY_SCHEMA)
		}

		return refCompChildId
	}

	return ({ refCompId }: Pick<ConsumeRefParams, 'refCompId'>) =>
		consumeRef({
			pageId: _.get(rawModel, [SITE_NAMESPACE, STRUCTURE_ASPECT, refCompId, 'metaData', 'pageId']),
			refCompId,
			refCompAncestorTemplateKey: SITE_NAMESPACE,
			refCompAncestorsChain: '',
			getAncestorsOverrideDataRefs: RETURN_EMPTY_ARRAY_FN,
			getAncestorsOverrideTranslationDataRefs: RETURN_EMPTY_ARRAY_FN,
			getAncestorsLeafsOverridesMapsRefs: RETURN_EMPTY_ARRAY_FN,
		})
}
